'use client'
import { type FC, useMemo } from 'react'
import { Box, SimpleGrid, type StackProps, Text, VStack } from '@chakra-ui/react'
import { z } from 'zod'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import type { CreateCustomerDTO } from 'ecosystem'
import {
  FormProvider,
  GrandFormInputField,
  GrandFormSubmitButton,
  type Resolver,
  useForm,
  zodResolver
} from '../grand-form'

export type SignupFormType = Omit<CreateCustomerDTO, 'type'>

export interface SignupFormOverrides {
  signupFormEmail?: string
  signupFormFirstName?: string
  signupFormLastName?: string
  signupFormBtnLabel?: string
  signupFormBtnLoading?: string
  signupFormEmailInvalid?: string
  signupFormFirstNameRequired?: string
  signupFormLastNameRequired?: string
}

interface SignupFormProps extends Omit<StackProps, 'onSubmit'> {
  onSubmit: (data: SignupFormType) => Promise<void> | void
  error?: string
  defaultValues?: Partial<SignupFormType>
}

const SignupForm: FC<SignupFormProps> = ({ onSubmit, error, defaultValues, ...stackProps }) => {
  const overrides = useOverridesContext<keyof SignupFormOverrides>()
  const emailLabel = overrideText('E-postadress', overrides?.signupFormEmail)
  const firstNameLabel = overrideText('Förnamn', overrides?.signupFormFirstName)
  const lastNameLabel = overrideText('Efternamn', overrides?.signupFormLastName)
  const btnLabel = overrideText('Registrera', overrides?.signupFormBtnLabel)
  const btnLoading = overrideText('Registrerar…', overrides?.signupFormBtnLoading)
  const emailInvalidMsg = overrideText(
    'Ange en giltig e-postadress.',
    overrides?.signupFormEmailInvalid
  )
  const firstNameRequired = overrideText('Förnamn krävs.', overrides?.signupFormFirstNameRequired)
  const lastNameRequired = overrideText('Efternamn krävs.', overrides?.signupFormLastNameRequired)

  const schema = useMemo(
    () =>
      z.object({
        email: z.email(emailInvalidMsg),
        firstName: z.string().min(1, firstNameRequired),
        lastName: z.string().min(1, lastNameRequired)
      }),
    [emailInvalidMsg, firstNameRequired, lastNameRequired]
  )

  const methods = useForm<SignupFormType>({
    defaultValues: { email: '', firstName: '', lastName: '', ...defaultValues },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema) as Resolver<SignupFormType>
  })

  return (
    <FormProvider {...methods}>
      <Box as="form" onSubmit={methods.handleSubmit(onSubmit)} w="full">
        <VStack align="stretch" spacing={4} w="full" {...stackProps}>
          <GrandFormInputField
            control={methods.control}
            label={emailLabel}
            name="email"
            placeholder={emailLabel}
            required
            type="email"
          />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <GrandFormInputField
              control={methods.control}
              label={firstNameLabel}
              name="firstName"
              placeholder={firstNameLabel}
              required
            />
            <GrandFormInputField
              control={methods.control}
              label={lastNameLabel}
              name="lastName"
              placeholder={lastNameLabel}
              required
            />
          </SimpleGrid>
          {error ? (
            <Text color="status.error" fontSize="sm">
              {error}
            </Text>
          ) : null}
          <GrandFormSubmitButton disableIfInvalid={false} loadingText={btnLoading}>
            {btnLabel}
          </GrandFormSubmitButton>
        </VStack>
      </Box>
    </FormProvider>
  )
}

export default SignupForm
